<template>
  <ion-content>
    <ion-refresher
      class="refresher refresher-machine-reports"
      @ionRefresh="doRefresh($event)"
      @click="doRefresh($event)"
      slot="fixed"
    >
      <ion-refresher-content></ion-refresher-content>
    </ion-refresher>

    <ion-item lines="none">
      <ion-icon slot="end" :icon="calendarNumberOutline"></ion-icon>
      <ion-select
        slot="end"
        @click="$emit('update:initialized', true)"
        v-model="selectedDate"
        placeholder="Aucun mois"
        ok-text="Valider"
        cancel-text="Annuler"
        interface="action-sheet"
      >
        <ion-select-option
          v-for="date in dates"
          :value="date[0]"
          :key="date[0]"
        >
          {{ date[1] }}
        </ion-select-option>
      </ion-select>
    </ion-item>

    <ion-card
      v-for="(week, index) in weeks"
      :key="index"
      class="week-card"
      @click="openDetail(week)"
    >
      <div class="report-week-total" v-if="week.totalHours">
        {{ floatToString(week.totalHours, 'h') }}
      </div>

      <ion-card-header>
        <ion-card-title class="text-align-center">
          <strong>
            du {{ week.formattedStart }} au {{ week.formattedEnd }}
          </strong>
        </ion-card-title>
      </ion-card-header>

      <ion-card-content>
        <ion-row class="day-selector">
          <div
            v-for="day in week.days"
            :key="day"
            @click.stop="openDetail(week, day)"
          >
            <div
              class="status"
              :class="{
                partial:
                  reports &&
                  reports[day.date] &&
                  reports[day.date].status === 'draft',
                full:
                  reports &&
                  reports[day.date] &&
                  reports[day.date].status === 'sent',
              }"
            ></div>

            <div class="day">
              {{ day.label }}

              <div
                class="number"
                :class="{
                  'has-admin-changes': hasAdminChanges(day.date),
                }"
              >
                {{ day.day }}
              </div>

              <span v-if="hasAdminChanges(day.date)" class="admin-changes-dot">
                🚩
              </span>
            </div>
          </div>
        </ion-row>
      </ion-card-content>
    </ion-card>
  </ion-content>
</template>

<script>
import dayjs from 'dayjs';
import axios from 'axios';
import { addOutline, calendarNumberOutline } from 'ionicons/icons';
import { getParams } from '@/services/users';
import { makeWeek } from '@/services/utils/reportUtils';
import { floatToString } from '@/services/utils/dateUtils';
import utils from '@/services/utils/Utils';
import {
  IonIcon,
  IonItem,
  IonRow,
  IonSelect,
  IonSelectOption,
} from '@ionic/vue';

export default {
  /**
   * This is a common list used for both time and machine reports.
   * "kind" prop determines the type of report. (and so the route used)
   */

  components: {
    IonIcon,
    IonItem,
    IonRow,
    IonSelect,
    IonSelectOption,
  },

  setup() {
    return {
      addOutline,
      calendarNumberOutline,
    };
  },

  data() {
    return {
      reports: {},
      weeks: [],
      selectedDate: new Date().toISOString().slice(0, 7),
    };
  },

  props: {
    kind: String,
  },

  async mounted() {
    this.initList();
  },

  methods: {
    initList() {
      const now = dayjs();

      // Parse the selected date to get the month and year
      const selectedMonth = dayjs(this.selectedDate);

      // Get start and end of the selected month
      const startOfMonth = selectedMonth.startOf('month');
      const endOfMonth = selectedMonth.endOf('month');

      // Find the start of the first week that contains days from this month
      const firstWeekStart = startOfMonth.startOf('week');

      // Calculate how many complete weeks we need to display for this month
      const weeksNeeded = Math.ceil(
        endOfMonth.diff(firstWeekStart, 'weeks', true),
      );

      // Generate the weeks array
      this.weeks = [];
      for (let i = weeksNeeded - 1; i >= 0; i--) {
        // For each week, calculate its start date and create the week object
        const weekStart = firstWeekStart.add(i, 'week');
        const weekData = makeWeek(weekStart);

        // Push only if week not in future
        if (weekStart.isBefore(now)) {
          this.weeks.push(weekData);
        }
      }
    },

    async getStatuses() {
      const requestConfig = await getParams();
      if (this.selectedDate) {
        requestConfig.params = { month: this.selectedDate };
      }

      const route =
        this.kind === 'time_report'
          ? '/driver/time-report'
          : '/driver/machine-report';

      const response = await axios.get(route, requestConfig);
      this.reports = response.data;
      this.computeTotals();
    },

    async doRefresh(event) {
      try {
        this.initList();
        await this.getStatuses();
        event.target.complete();
      } catch (e) {
        event.target.cancel();
      }
    },

    openDetail(week, day = null) {
      const route =
        this.kind === 'time_report' ? '/time-reports' : '/machine-reports';

      // if day is provided, use it, otherwise use the first day of the week
      const date = day ? day.date : week.days[0].date;
      this.$router.push(`${route}/${date}`);
    },

    computeTotals() {
      // Reset totals
      const newWeeks = this.weeks.map(week => ({
        ...week,
        totalHours: 0,
      }));

      // Sum hours for each week
      Object.keys(this.reports).forEach(dateKey => {
        const dayReport = this.reports[dateKey];
        if (dayReport.status !== 'sent') return;

        const weekNumber = dayjs(dateKey).week();
        const week = newWeeks.find(w => w.week === weekNumber);
        if (!week) return;

        const sum = (week.totalHours || 0) + (dayReport.hours_agent_total || 0);
        week.totalHours = sum;
      });

      // Apply the new weeks with totals
      this.weeks = newWeeks;
    },

    hasAdminChanges(date) {
      return !!(
        this.reports &&
        this.reports[date] &&
        this.reports[date].has_admin_changes
      );
    },

    floatToString,
  },

  watch: {
    selectedDate() {
      this.initList();
      this.getStatuses();
    },
  },

  computed: {
    dates() {
      return utils.generateDateList();
    },
  },
};
</script>

<style lang="scss" scoped>
.week-card {
  position: relative;
  padding-top: 6px;
  padding-bottom: 6px;
}

.report-week-total {
  position: absolute;
  top: 4px;
  left: 4px;
}

.ion-page {
  align-items: flex-end;
}

ion-item {
  ion-select {
    margin: 0;
  }

  ion-icon {
    margin-right: 8px;
  }
}

ion-card-title {
  color: #4c4639;
}

ion-content {
  --background: #fffcfa;
}

.day-selector {
  border: 2.5px solid #e8e1d9;
  border-radius: 4px;
  padding: 4px;

  .day {
    border: none;
    padding-bottom: 0;
    padding-top: 0;
  }
}

.has-admin-changes {
  color: rgb(214, 133, 109);
  font-weight: bold;
}
</style>
